import { useMemo } from "react";
import { Button, Typography, Breadcrumbs, Grid, Paper, Alert, AlertTitle } from "@mui/material";
import { IAircraft, IFullAircraftConfiguration, IAircraftConfigurationRevision } from "@vmosolutions/oc-interfaces";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import moment from "moment-timezone";
import SaveIcon from "@mui/icons-material/Save";
import { getLabel } from "./utils/index";

interface IAircraftContextProps {
  aircraft?: IAircraft;
  configs?: IFullAircraftConfiguration[];
  currentConfig?: IFullAircraftConfiguration;
  currentRevision?: IAircraftConfigurationRevision;
  totalConfigChanges?: number;
  totalRevisionChanges?: number;
  saveAirCraft?: any;
  saveAirCraftConfig?: any;
  saveAirCraftConfigRevision?: any;
  mode: "aircraft" | "config" | "revision";
  disabled?: boolean;
  dirty?: boolean;
  hasError?: boolean;
  stagedAircraft: IAircraft;
  stagedConfig?: IFullAircraftConfiguration[];
}

export function AircraftSaveBar(props: IAircraftContextProps): JSX.Element {
  const {
    aircraft,
    configs,
    currentConfig,
    currentRevision,
    saveAirCraft,
    saveAirCraftConfig,
    saveAirCraftConfigRevision,
    mode,
    disabled,
    dirty,
    hasError,
    stagedAircraft,
    stagedConfig,
  } = props;

  const getCurrentConfig = (): IFullAircraftConfiguration | undefined => {
    if (currentConfig && configs) {
      return configs.find((config: IFullAircraftConfiguration) => {
        return config._id === currentConfig?._id;
      });
    } else {
      return undefined;
    }
  };
  const getCurrentRevision = (): any | undefined => {
    if (getCurrentConfig() && currentRevision) {
      return getCurrentConfig()?.revisions.find((revision: any) => {
        return revision.effectiveDate === currentRevision?.effectiveDate;
      });
    } else {
      return undefined;
    }
  };
  const title = (): string => {
    switch (mode) {
      case "aircraft":
        return aircraft ? "Editing " + getLabel(aircraft) : "Staging New Aircraft";
        break;
      case "config": {
        const config = getCurrentConfig();
        return config ? "Editing " + config.title : stagedAircraft ? "Staging Configuration" : "Adding Configuration";
        break;
      }
      case "revision":
        return getCurrentRevision()
          ? "Creating New Revision From Previous Effective Date: " +
              moment(getCurrentRevision().effectiveDate).format("MMM DD, YYYY (HH:mmA)")
          : "Creating New Revision";
        break;
    }
  };
  const btnColor = useMemo(() => {
    if (dirty) {
      return "warning";
    } else if (hasError) {
      return "error";
    }
    return "success";
  }, [hasError, dirty]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Paper elevation={4} sx={{ p: 1 }}>
          <Grid container sx={{ alignItems: "center" }}>
            <Grid item xs={"auto"} sx={{ marginRight: 3 }}>
              <Button
                variant="contained"
                color={btnColor}
                disabled={disabled || !dirty}
                size="small"
                onClick={(): null => {
                  switch (mode) {
                    case "aircraft":
                      saveAirCraft();
                      break;
                    case "config":
                      saveAirCraftConfig();
                      break;
                    case "revision":
                      saveAirCraftConfigRevision();
                      break;
                  }
                  return null;
                }}
              >
                <SaveIcon />
                {!aircraft && mode !== "revision" ? "Stage" : "Save"}
              </Button>
            </Grid>
            {aircraft && (
              <Grid item xs>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                  <Typography>{getLabel(aircraft)}</Typography>
                  {getCurrentConfig() ? <Typography>{getCurrentConfig()?.title}</Typography> : "NEW"}
                  {getCurrentRevision() ? (
                    <Typography>
                      {moment(getCurrentRevision()?.effectiveDate).format("MMM DD, YYYY (HH:mmA)")}
                    </Typography>
                  ) : (
                    "NEW"
                  )}
                </Breadcrumbs>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Typography sx={{ fontWeight: 900, fontSize: "1.4em", py: 2 }}>{title()}</Typography>
      </Grid>
      {mode === "aircraft" && !aircraft && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Alert severity="info" color="info" variant="filled">
            <AlertTitle color="common.white">Adding Configurations</AlertTitle>After saving a new aircraft, you will
            automatically be shown a form, to add a new configuration. Your changes are staged, but not saved, until you
            add a configuration revision.
          </Alert>
        </Grid>
      )}
      {mode === "config" && !getCurrentConfig() && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Alert severity="info" color="info" variant="filled">
            <AlertTitle color="common.white">Adding Revisions</AlertTitle>After saving a new configuration, you will
            automatically be shown a form, to add a new revision of that configuration
          </Alert>
        </Grid>
      )}
    </Grid>
  );
}
